import type {TokenSerializer} from "@/generated/DjangoTypes";
import {resetAllStores} from "@/store/createStore";
import {acceptLanguageAtom} from "@/store/jotaiAtoms";
import {TOKEN_STORE_KEY, useTokenStore} from "@/store/token";
import axios from "axios";
import createAuthRefreshInterceptor, {type AxiosAuthRefreshRequestConfig} from "axios-auth-refresh";
import {getDefaultStore} from "jotai";

const setTokens = (tokens: TokenSerializer) => {
  useTokenStore.getState().setAccessToken(tokens.access);
  useTokenStore.getState().setRefreshToken(tokens.refresh);
};

const getTokensFromStorage = (): {access: string | null; refresh: string | null} => {
  try {
    const stored = localStorage.getItem(TOKEN_STORE_KEY);
    if (!stored) return {access: null, refresh: null};
    const state = JSON.parse(stored)?.state;
    return {access: state?.access ?? null, refresh: state?.refresh ?? null};
  } catch {
    return {access: null, refresh: null};
  }
};

const syncTokensFromLocalStorage = () => {
  const {access, refresh} = getTokensFromStorage();
  if (access) useTokenStore.getState().setAccessToken(access);
  if (refresh) useTokenStore.getState().setRefreshToken(refresh);
};

const baseUrl = import.meta.env.VITE_APP_API_BASE_URL ? `${import.meta.env.VITE_APP_API_BASE_URL}/api/` : "/api/";

const handleRefreshFailure = () => {
  const isLoggedIn = !!useTokenStore.getState().userId;

  if (isLoggedIn) {
    useTokenStore.getState().reset();
    resetAllStores();

    if (!window.location.pathname.startsWith("/login")) {
      const target = `${window.location.pathname}${window.location.search}`;
      const redirectParam = target && target !== "/" ? `?redirect=${encodeURIComponent(target)}` : "";
      window.location.href = `${window.location.origin}/login/${redirectParam}`;
    }
  }
};

const refreshAuthToken = async () => {
  const stored = getTokensFromStorage();

  try {
    const response = await axios.post<TokenSerializer>(baseUrl + "token/refresh/", {refresh: stored.refresh}, {
      skipAuthRefresh: true,
    } as AxiosAuthRefreshRequestConfig);

    setTokens(response.data);
  } catch {
    const latest = getTokensFromStorage();
    if (latest.refresh && latest.refresh !== stored.refresh) {
      syncTokensFromLocalStorage();
      return;
    }

    handleRefreshFailure();
  }
};

// Refresh the token under the same mutex the 401 interceptor uses, so a
// proactive refresh (e.g. an auth guard swapping out an expired token) can
// never race a concurrent 401-triggered refresh. The backend recomputes
// access_level / agency_ids / sub_brand_ids on refresh, so this also heals
// stale claims baked into an old token.
export const refreshTokensWithLock = () =>
  navigator?.locks ? navigator.locks.request("token-refresh", refreshAuthToken) : refreshAuthToken();

const AxiosInstance = axios.create({
  baseURL: baseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

AxiosInstance.interceptors.request.use(request => {
  const accessToken = useTokenStore.getState().access;
  const lang = getDefaultStore().get(acceptLanguageAtom);

  if (request.headers) {
    request.headers["Accept-Language"] = lang;

    if (accessToken) {
      request.headers["Authorization"] = `Bearer ${accessToken}`;
    }
  } else {
    Object.assign(request, {
      headers: {
        "Accept-Language": lang,
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
    });
  }

  return request;
});

createAuthRefreshInterceptor(AxiosInstance, () => refreshTokensWithLock());

const client = AxiosInstance;

export {client};
